/*
 * halal-label/ext-render.js — the two things the label-maker engine does not
 * draw, drawn by BOTH backends from the same numbers:
 *
 *   italic text   the ING footnote must be italic (PerBPOM 10/2026 Lamp. I
 *                 angka 6 huruf a). Noto Sans as shipped has no italic file in
 *                 the shared face table, so both backends draw the upright
 *                 face as an OBLIQUE of the same angle: SVG skewX, PDF text
 *                 matrix shear. Same glyphs, same advances, same slant.
 *   vector path   the NON HALAL / MENGANDUNG BABI marks traced from BPJPH's
 *                 own files (resources/data/halal-label/non-halal-marks.json),
 *                 on their white (or contrast) ground.
 *
 * The PDF side is an OVERLAY on the file LabelPdf.build() made: same cell
 * placement (LabelSheets.cellAt), same printer correction scale `sx` — the
 * very arithmetic of label-maker/pdf.js drawCell — so the two layers cannot
 * drift apart. Paths go through window.__pdfSvgPath (long paths overflow
 * pdf-lib's argument list otherwise).
 */
(function (root, factory) {
    'use strict';
    var api = factory();
    if (typeof module !== 'undefined' && module.exports) module.exports = api;
    if (root) root.HalalExt = api;
})(typeof self !== 'undefined' ? self : (typeof window !== 'undefined' ? window : null), function () {
    'use strict';

    var NS = 'http://www.w3.org/2000/svg';
    var PT = 72 / 25.4;
    var OBLIQUE_DEG = 12;

    function el(name, attrs) {
        var n = document.createElementNS(NS, name);
        for (var k in attrs) if (Object.prototype.hasOwnProperty.call(attrs, k) && attrs[k] != null) n.setAttribute(k, String(attrs[k]));
        return n;
    }

    /* ── SVG (preview) ───────────────────────────────────────────────────── */
    function svg(target, ext, opts) {
        opts = opts || {};
        var g = el('g', { 'data-hl-ext': '1' });
        (ext || []).forEach(function (it) {
            var b = it.box;
            if (it.kind === 'path') {
                if (it.bg) g.appendChild(el('rect', { x: b.x, y: b.y, width: b.w, height: b.h, fill: it.bg }));
                var p = el('path', {
                    d: it.d, fill: it.fill,
                    transform: 'translate(' + b.x + ' ' + b.y + ') scale(' + (b.w / it.vw) + ' ' + (b.h / it.vh) + ')',
                });
                g.appendChild(p);
                return;
            }
            if (it.kind === 'text') {
                var base = b.y + it.size * (it.ascentEm || 0.8);
                var t = el('text', {
                    x: 0, y: 0,
                    transform: 'translate(' + b.x + ' ' + base + ') skewX(' + (-OBLIQUE_DEG) + ')',
                    'font-family': opts.fontFamilyFor ? opts.fontFamilyFor(it.font) : 'sans-serif',
                    'font-size': it.size,
                    'font-weight': it.bold ? 700 : 400,
                    fill: it.color || '#000',
                    'xml:space': 'preserve',
                    style: 'font-feature-settings:"calt" 0',
                });
                t.textContent = it.lines[0];
                g.appendChild(t);
            }
        });
        target.appendChild(g);
        return g;
    }

    /* ── PDF (overlay) ───────────────────────────────────────────────────── */
    function rgb(PL, hex) {
        var h = String(hex || '#000000').replace('#', '');
        if (h.length === 3) h = h.split('').map(function (c) { return c + c; }).join('');
        var n = parseInt(h, 16);
        return PL.rgb(((n >> 16) & 255) / 255, ((n >> 8) & 255) / 255, (n & 255) / 255);
    }

    /* job: { fmt, cells:[{ext:[…]}|null], order, correction, title, lang }
       pdfBytes: what LabelPdf.build() produced. Returns Promise<Uint8Array>. */
    function overlay(pdfBytes, job) {
        var PL = window.PDFLib;
        var S = window.LabelSheets;
        var TF = window.PdfTextFont;
        return PL.PDFDocument.load(pdfBytes).then(function (doc) {
            if (job.title) doc.setTitle(job.title);
            doc.setLanguage(job.lang || 'id-ID');
            doc.setCreator('Timbrica halal-label');
            var fmt = job.fmt;
            var pageMm = S.page(fmt.page);
            var perSheet = S.perSheet(fmt);
            var sx = S.correction(job.correction).scale;
            var pages = doc.getPages();

            // Text that needs a font: embed once, over the union of strings.
            var need = { r: '', b: '' };
            (job.cells || []).forEach(function (c) {
                (c && c.ext || []).forEach(function (it) { if (it.kind === 'text') need[it.bold ? 'b' : 'r'] += it.lines[0]; });
            });
            var fonts = {};
            var chain = Promise.resolve();
            ['r', 'b'].forEach(function (k) {
                if (!need[k]) return;
                chain = chain.then(function () {
                    return TF.ready(need[k]).then(function () {
                        var face = TF.canRender(need[k], 'noto') ? 'noto' : TF.pick(need[k]);
                        return TF.embed(doc, face, { bold: k === 'b', subset: true });
                    }).then(function (f) { fonts[k] = f; });
                });
            });
            return chain.then(function () {
                (job.cells || []).forEach(function (c, idx) {
                    if (!c || !c.ext || !c.ext.length) return;
                    var page = pages[Math.floor(idx / perSheet)];
                    if (!page) return;
                    var cell = S.cellAt(fmt, idx % perSheet, { order: job.order, correction: job.correction });
                    c.ext.forEach(function (it) { draw(PL, page, pageMm, cell, sx, it, fonts); });
                });
                return doc.save({ useObjectStreams: true });
            });
        });
    }

    function draw(PL, page, pageMm, cell, sx, it, fonts) {
        var bx = cell.x + it.box.x * sx, by = cell.y + it.box.y * sx;
        var bw = it.box.w * sx, bh = it.box.h * sx;
        var X = function (mm) { return mm * PT; };
        var Y = function (mm) { return (pageMm.h - mm) * PT; };
        if (it.kind === 'path') {
            if (it.bg) page.drawRectangle({ x: X(bx), y: Y(by + bh), width: bw * PT, height: bh * PT, color: rgb(PL, it.bg) });
            var opts = { x: X(bx), y: Y(by), scale: (bw / it.vw) * PT, color: rgb(PL, it.fill), borderWidth: 0 };
            // the site's door for paths of any length (timbrica-core.js)
            window.__pdfSvgPath(page, it.d, opts, PL);
            return;
        }
        if (it.kind === 'text') {
            var f = fonts[it.bold ? 'b' : 'r'];
            if (!f) return;
            var size = it.size * sx;
            var base = by + size * (it.ascentEm || 0.8);
            // Text matrix [1 0 tan(a) 1 x y]: x shifts with y → an oblique.
            page.drawText(it.lines[0], {
                x: X(bx), y: Y(base), size: size * PT, font: f, color: rgb(PL, it.color),
                ySkew: PL.degrees(OBLIQUE_DEG),
            });
        }
    }

    return { svg: svg, overlay: overlay, OBLIQUE_DEG: OBLIQUE_DEG };
});
